import React, { useMemo } from 'react';
import classNames from 'classnames';
import { useThemeVarsClass } from '../../../theme/provider';
import { useFilterBar } from '../hooks/useFilterBar';
import { FilterItem } from '../FilterItem';
import { AddFilterButton } from '../AddFilterButton';
import {
  DEFAULT_FILTER_BAR_LABELS,
  FilterBarContextProvider,
} from '../state/FilterBarContext';
import type {
  FilterBarController,
  FilterBarProps,
  FilterItemConfig,
  FilterValues,
} from '../types';

import cn from './styles.module.scss';

/**
 * Top-level FilterBar component.
 *
 * Modes:
 *  - **Uncontrolled (default)**: `defaultValues` seeds; FilterBar owns state via
 *    `useFilterBar` internally.
 *  - **Controlled**: pass `values` prop; FilterBar reflects it via prop-watching
 *    effect with one-level-deep array equality (echo-silence).
 *  - **External controller**: pass `controller` (output of caller's own
 *    `useFilterBar` call); FilterBar renders over that hook's state.
 */
export function FilterBar<T extends FilterValues = FilterValues>(props: FilterBarProps<T>) {
  // If the caller passed an external controller, we don't run our own hook.
  if (props.controller) {
    return <ExternalControllerFilterBar {...props} controller={props.controller} />;
  }
  return <InternalControllerFilterBar {...props} />;
}

function InternalControllerFilterBar<T extends FilterValues>(props: FilterBarProps<T>) {
  const controller = useFilterBar<T>({
    items: props.items,
    initialValues: props.defaultValues,
    values: props.values,
    persistence: props.persistence,
    autoSave: props.autoSave,
    initialVisibleFields: props.initialVisibleFields,
    onInit: props.onInit,
    onChange: props.onChange,
    onFieldChange: props.onFieldChange,
  });
  return <FilterBarShell {...props} controller={controller} />;
}

function ExternalControllerFilterBar<T extends FilterValues>(props: FilterBarProps<T> & { controller: FilterBarController<T> }) {
  return <FilterBarShell {...props} />;
}

function FilterBarShell<T extends FilterValues>(props: FilterBarProps<T> & { controller: FilterBarController<T> }) {
  const {
    items,
    controller,
    size = 'middle',
    addable = true,
    availableToAdd,
    showClearAll = true,
    showResetAll = false,
    isLoadingExternal = false,
    leading,
    trailing,
    labels: labelsProp,
    className,
  } = props;
  const themeClass = useThemeVarsClass();

  const labels = useMemo(() => ({ ...DEFAULT_FILTER_BAR_LABELS, ...labelsProp }), [labelsProp]);

  const itemsByName = useMemo(() => {
    const out: Record<string, FilterItemConfig> = {};
    for (const item of items) out[item.name] = item;
    return out;
  }, [items]);

  const ctxValue = useMemo(
    () => ({
      controller: controller as FilterBarController,
      size,
      labels,
      isReady: controller.isReady,
      isLoadingExternal,
      itemsByName,
    }),
    [controller, size, labels, isLoadingExternal, itemsByName],
  );

  const visibleItems = useMemo(() => {
    const visibleSet = new Set(controller.visibleFields);
    return items
      .filter((item) => visibleSet.has(item.name) || item.pinned)
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  }, [items, controller.visibleFields]);

  const gated = !controller.isReady || isLoadingExternal;

  return (
    <FilterBarContextProvider value={ctxValue}>
      <div
        role="region"
        aria-label={labels.barAriaLabel}
        aria-busy={gated || undefined}
        className={classNames(cn.root, themeClass, className)}
        data-testid="filter-bar"
      >
        <div
          className={classNames(cn.bar, {
            [cn.gateOverlay]: gated,
          })}
        >
          {leading && <div className={cn.leading}>{leading}</div>}
          {visibleItems.map((item) => (
            <FilterItem key={item.name} name={item.name} />
          ))}
          {addable && <AddFilterButton availableToAdd={availableToAdd} />}
          {showClearAll && (
            <button
              type="button"
              className={cn.clearAllButton}
              onClick={() => controller.clearAll()}
              aria-label={labels.clearAll}
              data-testid="filter-bar-clear-all"
            >
              {labels.clearAll}
            </button>
          )}
          {showResetAll && (
            <button
              type="button"
              className={cn.clearAllButton}
              onClick={() => controller.resetAll()}
              aria-label={labels.resetAll}
              data-testid="filter-bar-reset-all"
            >
              {labels.resetAll}
            </button>
          )}
          {trailing && <div className={cn.trailing}>{trailing}</div>}
        </div>
      </div>
    </FilterBarContextProvider>
  );
}
